import React from 'react';
import { useFormContext, Controller } from 'react-hook-form';
import AsyncSelect from 'react-select/async';
import { useTranslate } from '../TranslationText';

/**
 * Clean async select dropdown with React Hook Form integration.
 * Uses react-select/async directly.
 *
 * @param {string} name - Form field name
 * @param {string} label - Label text
 * @param {function} loadOptions - Async function returning a promise with options
 * @param {string} inputValue - Current input value for searching
 * @param {function} onInputChange - Callback when input value changes
 * @param {boolean} required - Show required asterisk
 * @param {boolean} disabled - Disable select
 * @param {object} rules - Validation rules
 * @param {boolean} isClearable - Allow clearing selection
 * @param {boolean} hideLabel - Hide the label
 * @param {function} onChangeCallback - Optional callback after value changes
 * @param {string} placeholder - Select placeholder
 */
export default function FormAsyncSelect({
    name,
    label,
    loadOptions,
    inputValue,
    onInputChange,
    required = false,
    disabled = false,
    rules,
    isClearable = false,
    hideLabel = false,
    onChangeCallback,
    placeholder,
    containerClass = "mb-2",
    noValidate = false,
}) {
    const { control, formState: { errors } } = useFormContext();
    const { translate } = useTranslate();

    const validationRules = {
        ...(required && !noValidate && {
            required: translate('web.requiredField', 'Required Field'),
        }),
        ...rules,
    };

    const fieldError = name.split('.').reduce((obj, key) => obj?.[key], errors);

    return (
        <div className={containerClass}>
            {!hideLabel && label && (
                <label htmlFor={name} className="capatalize_text">
                    {label}{' '}
                    {required && <i style={{ color: 'red', margin: 'auto 5px' }}>*</i>}
                </label>
            )}

            <Controller
                name={name}
                control={control}
                rules={validationRules}
                render={({ field }) => (
                    <AsyncSelect
                        inputId={name}
                        ref={field.ref}
                        loadOptions={loadOptions}
                        onInputChange={onInputChange}
                        inputValue={inputValue}
                        value={field.value}
                        onChange={(selected) => {
                            field.onChange(selected);
                            if (onChangeCallback) onChangeCallback(selected);
                        }}
                        onBlur={field.onBlur}
                        isDisabled={disabled}
                        isClearable={isClearable}
                        placeholder={placeholder ?? translate('web.select', 'Select')}
                        noOptionsMessage={() =>
                            translate('web.noOptions', 'No Options')
                        }
                        loadingMessage={() =>
                            translate('web.loading', 'Loading...')
                        }
                    />
                )}
            />

            {fieldError && (
                <div style={{ color: 'red', marginTop: '5px' }}>
                    {fieldError.message}
                </div>
            )}
        </div>
    );
}
